import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  AlertCircle,
  CheckCircle2,
  ExternalLink,
  Eye,
  EyeOff,
  Loader2,
  Mail,
  RefreshCw,
  Trash2,
} from 'lucide-react'
import { useToast } from '@/components/Toast'
import { JarvisCard } from '@/components/JarvisCard'
import { VoiceCard } from '@/components/VoiceCard'
import { SetupWizard } from '@/components/setup/SetupWizard'
import { useConnections, useCurrentUser } from '@/lib/hooks'
import { apiClient, unwrap } from '@/lib/apiClient'
import type { ConnectionProvider, ConnectionStatus } from '@/types'
import { cn } from '@/lib/cn'

function formatRelativeTime(iso: string): string {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return iso
  const diff = Date.now() - then
  const minutes = Math.round(diff / 60_000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.round(hours / 24)
  if (days < 30) return `${days}d ago`
  return new Date(iso).toLocaleDateString()
}

interface ProviderSpec {
  provider: ConnectionProvider
  title: string
  blurb: string
  required: boolean
  helpUrl?: string
  helpLabel?: string
  fields: Array<{
    key: string
    label: string
    placeholder?: string
    secret?: boolean
    hint?: string
  }>
}

const PROVIDERS: ProviderSpec[] = [
  {
    provider: 'facebook',
    title: 'Facebook Ads',
    blurb:
      'Connect your Meta Ads account so Jarvis can pull campaign data and surface scale/pause recommendations.',
    required: true,
    helpUrl: 'https://business.facebook.com/business/loginpage/',
    helpLabel: 'Get a System User token',
    fields: [
      {
        key: 'systemUserToken',
        label: 'System User Token',
        placeholder: 'EAAB…',
        secret: true,
        hint: 'In Meta Business Settings → System Users, generate a token with ads_read scope.',
      },
      {
        key: 'adAccountId',
        label: 'Ad Account ID',
        placeholder: 'act_1234567890',
        hint: 'Find this at the top of your Ads Manager URL.',
      },
    ],
  },
  {
    provider: 'stripe',
    title: 'Stripe',
    blurb: 'Connect Stripe so Jarvis can show MRR, trials, churn, and run revenue briefings.',
    required: true,
    helpUrl: 'https://dashboard.stripe.com/apikeys',
    helpLabel: 'Get a restricted key',
    fields: [
      {
        key: 'secretKey',
        label: 'Secret Key',
        placeholder: 'sk_live_… or sk_test_…',
        secret: true,
        hint: 'Use a restricted key with read-only access to Customers, Subscriptions, and Invoices.',
      },
      {
        key: 'publishableKey',
        label: 'Publishable Key (optional)',
        placeholder: 'pk_live_…',
      },
    ],
  },
  {
    provider: 'foundry',
    title: 'Azure AI Foundry (Claude Sonnet 4.6)',
    blurb:
      'Optional but recommended. Connect your AI Foundry deployment so Jarvis can analyze ads and answer questions.',
    required: false,
    helpUrl: 'https://ai.azure.com',
    helpLabel: 'Open AI Foundry',
    fields: [
      {
        key: 'endpoint',
        label: 'Endpoint',
        placeholder: 'https://{resource}.cognitiveservices.azure.com',
      },
      {
        key: 'apiKey',
        label: 'API Key',
        placeholder: '…',
        secret: true,
      },
      {
        key: 'deploymentName',
        label: 'Deployment Name',
        placeholder: 'claude-sonnet-4-6',
      },
    ],
  },
]

interface ProviderCardProps {
  spec: ProviderSpec
  current: ConnectionStatus | undefined
}

function ProviderCard({ spec, current }: ProviderCardProps) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({})
  const [values, setValues] = useState<Record<string, string>>({})
  const [editing, setEditing] = useState(!current)

  const save = useMutation({
    mutationFn: () =>
      unwrap<ConnectionStatus>(
        apiClient.post('/setup/connections', {
          provider: spec.provider,
          [spec.provider]: values,
        }),
      ),
    onSuccess: (data) => {
      setValues({})
      setEditing(false)
      queryClient.invalidateQueries({ queryKey: ['connections'] })
      if (data.status === 'connected') {
        toast.success(`${spec.title} connected`)
      } else {
        toast.error(`${spec.title} saved but invalid`, data.lastError ?? undefined)
      }
    },
    onError: (e) => toast.error(`Couldn't save ${spec.title}`, (e as Error).message),
  })

  const remove = useMutation({
    mutationFn: () =>
      unwrap(apiClient.delete(`/setup/connections/${spec.provider}`)),
    onSuccess: () => {
      setValues({})
      setEditing(true)
      queryClient.invalidateQueries({ queryKey: ['connections'] })
      toast.success(`${spec.title} disconnected`)
    },
  })

  const verify = useMutation({
    mutationFn: () =>
      unwrap<ConnectionStatus>(
        apiClient.post(`/setup/connections/${spec.provider}/verify`),
      ),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['connections'] })
      if (data.status === 'connected') {
        toast.success(`${spec.title} reachable`, 'Credentials verified live.')
      } else {
        toast.error(`${spec.title} unreachable`, data.lastError ?? undefined)
      }
    },
    onError: (e) => toast.error('Verification failed', (e as Error).message),
  })

  const isConnected = current?.status === 'connected'
  const isInvalid = current?.status === 'invalid' || current?.status === 'error'

  return (
    <JarvisCard className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold">{spec.title}</h3>
            {spec.required && (
              <span className="text-[10px] uppercase tracking-widest text-jarvis-amber bg-jarvis-amber/10 border border-jarvis-amber/20 rounded px-1.5 py-0.5">
                Required
              </span>
            )}
          </div>
          <p className="text-sm text-jarvis-muted mt-1">{spec.blurb}</p>
        </div>
        <StatusIndicator status={current?.status} />
      </div>

      {current && !editing && (
        <div className="space-y-2 pt-2 border-t border-jarvis-border/50">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-jarvis-muted">
            {Object.entries(current.metadata).map(([k, v]) => (
              <span key={k} className="tabular max-w-[220px] truncate" title={String(v)}>
                <span className="uppercase tracking-widest">{k}</span>{' '}
                <span className="text-jarvis-text">{v}</span>
              </span>
            ))}
          </div>
          {current.lastVerifiedAt && (
            <p className="text-xs text-jarvis-muted">
              Verified {formatRelativeTime(current.lastVerifiedAt)}
            </p>
          )}
          {isInvalid && current.lastError && (
            <div className="flex items-start gap-2 text-sm text-jarvis-red">
              <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
              <span>{current.lastError}</span>
            </div>
          )}
          <div className="flex gap-2 pt-1">
            <button
              onClick={() => verify.mutate()}
              disabled={verify.isPending}
              className="text-xs px-3 py-1.5 bg-jarvis-surface-2 border border-jarvis-border rounded-lg hover:bg-jarvis-blue/10 hover:border-jarvis-blue/40 hover:text-jarvis-blue transition-colors flex items-center gap-1.5 disabled:opacity-50"
            >
              <RefreshCw size={12} className={verify.isPending ? 'animate-spin' : ''} />
              {spec.provider === 'stripe'
                ? verify.isPending
                  ? 'Verifying…'
                  : 'Verify & Sync'
                : verify.isPending
                  ? 'Verifying…'
                  : 'Test connection'}
            </button>
            <button
              onClick={() => setEditing(true)}
              className="text-xs px-3 py-1.5 bg-jarvis-surface-2 border border-jarvis-border rounded-lg hover:bg-jarvis-surface transition-colors"
            >
              Update
            </button>
            <button
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
              className="text-xs px-3 py-1.5 bg-jarvis-surface-2 border border-jarvis-border rounded-lg hover:bg-jarvis-red/10 hover:border-jarvis-red/30 hover:text-jarvis-red transition-colors flex items-center gap-1.5"
            >
              <Trash2 size={12} />
              Disconnect
            </button>
          </div>
        </div>
      )}

      {(editing || !current) && (
        <div className="space-y-3 pt-2 border-t border-jarvis-border/50">
          {spec.fields.map((field) => (
            <label key={field.key} className="block">
              <span className="text-xs uppercase tracking-widest text-jarvis-muted mb-1.5 block">
                {field.label}
              </span>
              <div className="relative">
                <input
                  type={field.secret && !showSecrets[field.key] ? 'password' : 'text'}
                  value={values[field.key] ?? ''}
                  onChange={(e) =>
                    setValues({ ...values, [field.key]: e.target.value })
                  }
                  placeholder={field.placeholder}
                  className="bg-jarvis-surface-2 border border-jarvis-border rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:border-jarvis-blue/60 font-mono"
                />
                {field.secret && (
                  <button
                    type="button"
                    onClick={() =>
                      setShowSecrets({
                        ...showSecrets,
                        [field.key]: !showSecrets[field.key],
                      })
                    }
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-jarvis-muted hover:text-jarvis-text"
                  >
                    {showSecrets[field.key] ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                )}
              </div>
              {field.hint && (
                <p className="text-xs text-jarvis-muted mt-1">{field.hint}</p>
              )}
            </label>
          ))}

          {save.error && (
            <div className="flex items-start gap-2 text-sm text-jarvis-red">
              <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
              <span>{(save.error as Error).message}</span>
            </div>
          )}

          <div className="flex items-center justify-between gap-3 pt-1">
            {spec.helpUrl ? (
              <a
                href={spec.helpUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs text-jarvis-blue hover:underline"
              >
                {spec.helpLabel ?? 'Open docs'} <ExternalLink size={12} />
              </a>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              {current && (
                <button
                  onClick={() => {
                    setEditing(false)
                    setValues({})
                  }}
                  className="text-xs px-3 py-1.5 bg-jarvis-surface-2 border border-jarvis-border rounded-lg hover:bg-jarvis-surface transition-colors"
                >
                  Cancel
                </button>
              )}
              <button
                onClick={() => save.mutate()}
                disabled={
                  save.isPending ||
                  spec.fields
                    .filter((f) => f.secret || !f.label.toLowerCase().includes('optional'))
                    .every((f) => !values[f.key]?.trim())
                }
                className="text-xs px-4 py-1.5 bg-jarvis-blue hover:bg-jarvis-blue-bright disabled:opacity-50 rounded-lg font-medium transition-colors flex items-center gap-1.5"
              >
                {save.isPending && <Loader2 size={12} className="animate-spin" />}
                {isConnected ? 'Save changes' : 'Connect'}
              </button>
            </div>
          </div>
        </div>
      )}
    </JarvisCard>
  )
}

interface GoogleCardProps {
  current: ConnectionStatus | undefined
}

function GoogleCard({ current: connection }: GoogleCardProps) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const isConnected = connection?.status === 'connected'
  const email = connection?.metadata?.email

  const start = useMutation({
    mutationFn: () =>
      unwrap<{ url: string }>(apiClient.get('/setup/google/auth')),
    onSuccess: (data) => {
      window.location.href = data.url
    },
    onError: (e) => toast.error('Could not start Google OAuth', (e as Error).message),
  })

  const verify = useMutation({
    mutationFn: () =>
      unwrap<ConnectionStatus>(
        apiClient.post('/setup/connections/google/verify'),
      ),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['connections'] })
      if (data.status === 'connected') {
        toast.success('Google reachable', `Authenticated as ${data.metadata.email}`)
      } else {
        toast.error('Google unreachable', data.lastError ?? undefined)
      }
    },
  })

  const remove = useMutation({
    mutationFn: () => unwrap(apiClient.delete('/setup/connections/google')),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connections'] })
      toast.success('Google disconnected')
    },
  })

  return (
    <JarvisCard className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold">Google Workspace</h3>
          </div>
          <p className="text-sm text-jarvis-muted mt-1">
            Let Jarvis read &amp; send Gmail, plus read and create Calendar events.
          </p>
        </div>
        <StatusIndicator status={connection?.status} />
      </div>

      {isConnected ? (
        <div className="space-y-3 pt-2 border-t border-jarvis-border/50">
          <div className="flex items-center gap-2 text-sm">
            <Mail size={14} className="text-jarvis-blue" />
            <span className="text-jarvis-text truncate" title={email}>{email ?? 'Connected'}</span>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-jarvis-muted">
            <span>✓ Read emails</span>
            <span>✓ Send emails</span>
            <span>✓ Read calendar</span>
            <span>✓ Create events</span>
          </div>
          {connection?.lastVerifiedAt && (
            <p className="text-xs text-jarvis-muted">
              Verified {formatRelativeTime(connection.lastVerifiedAt)}
            </p>
          )}
          <div className="flex gap-2 pt-1">
            <button
              onClick={() => verify.mutate()}
              disabled={verify.isPending}
              className="text-xs px-3 py-1.5 bg-jarvis-surface-2 border border-jarvis-border rounded-lg hover:bg-jarvis-blue/10 hover:border-jarvis-blue/40 hover:text-jarvis-blue transition-colors flex items-center gap-1.5 disabled:opacity-50"
            >
              <RefreshCw size={12} className={verify.isPending ? 'animate-spin' : ''} />
              {verify.isPending ? 'Verifying…' : 'Verify connection'}
            </button>
            <button
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
              className="text-xs px-3 py-1.5 bg-jarvis-surface-2 border border-jarvis-border rounded-lg hover:bg-jarvis-red/10 hover:border-jarvis-red/30 hover:text-jarvis-red transition-colors flex items-center gap-1.5"
            >
              <Trash2 size={12} />
              Disconnect
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3 pt-2 border-t border-jarvis-border/50">
          <button
            onClick={() => start.mutate()}
            disabled={start.isPending}
            className="w-full py-2.5 bg-white text-gray-800 rounded-lg font-medium text-sm flex items-center justify-center gap-2 hover:bg-gray-50 disabled:opacity-50"
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4" xmlns="http://www.w3.org/2000/svg">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.99 10.99 0 0 0 12 23z" fill="#34A853"/>
              <path d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18A10.99 10.99 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.83z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" fill="#EA4335"/>
            </svg>
            {start.isPending ? 'Redirecting…' : 'Connect Google account'}
          </button>
          <p className="text-xs text-jarvis-muted">
            You'll be redirected to Google to grant access. Tokens are encrypted with the
            master key in Key Vault.
          </p>
        </div>
      )}
    </JarvisCard>
  )
}

interface ComingSoonItem {
  name: string
  label: string
  icon: string
}

const COMING_SOON: ComingSoonItem[] = [
  { name: 'Microsoft 365', label: 'Outlook · Teams', icon: 'M' },
  { name: 'QuickBooks', label: 'Accounting', icon: 'Q' },
  { name: 'HubSpot', label: 'CRM', icon: 'H' },
  { name: 'Shopify', label: 'E-commerce', icon: 'S' },
  { name: 'Calendly', label: 'Scheduling', icon: 'C' },
  { name: 'Notion', label: 'Workspace', icon: 'N' },
]

function ComingSoonStrip() {
  return (
    <section className="mt-2">
      <h2 className="text-xs uppercase tracking-widest text-jarvis-muted mb-3">
        Coming Soon
      </h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {COMING_SOON.map((item) => (
          <div
            key={item.name}
            className="flex items-center gap-3 rounded-lg border border-jarvis-border bg-jarvis-surface p-3 opacity-60 cursor-not-allowed select-none"
          >
            <div className="w-8 h-8 rounded-md bg-jarvis-surface-2 border border-jarvis-border flex items-center justify-center text-jarvis-muted font-semibold text-sm">
              {item.icon}
            </div>
            <div className="min-w-0">
              <p className="text-jarvis-text text-sm font-medium truncate">{item.name}</p>
              <p className="text-jarvis-muted text-xs truncate">{item.label}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}

function StatusIndicator({ status }: { status?: ConnectionStatus['status'] }) {
  if (status === 'connected') {
    return (
      <span className="flex items-center gap-1.5 text-xs text-jarvis-green flex-shrink-0">
        <CheckCircle2 size={14} />
        Connected
      </span>
    )
  }
  if (status === 'invalid' || status === 'error') {
    return (
      <span className="flex items-center gap-1.5 text-xs text-jarvis-red flex-shrink-0">
        <AlertCircle size={14} />
        Invalid
      </span>
    )
  }
  return (
    <span className="flex items-center gap-1.5 text-xs text-jarvis-muted flex-shrink-0">
      <span className="w-1.5 h-1.5 rounded-full bg-jarvis-muted" />
      Not connected
    </span>
  )
}

export function Setup() {
  const { data, isLoading } = useConnections()
  const { data: user } = useCurrentUser()
  const queryClient = useQueryClient()
  const toast = useToast()
  const items = data?.items ?? []

  // Google OAuth callback bounce — googleCallback.ts redirects here with
  // ?googleConnected=1 or ?googleError=<msg>. Surface a toast and clean the URL.
  useEffect(() => {
    const url = new URL(window.location.href)
    const connected = url.searchParams.get('googleConnected')
    const err = url.searchParams.get('googleError')
    if (connected) {
      toast.success('Google connected')
      queryClient.invalidateQueries({ queryKey: ['connections'] })
      url.searchParams.delete('googleConnected')
      window.history.replaceState({}, '', url.toString())
    } else if (err) {
      toast.error('Google connect failed', err)
      url.searchParams.delete('googleError')
      window.history.replaceState({}, '', url.toString())
    }
    // intentionally run once on mount only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // First-time users see the wizard; returning users see the connection cards.
  if (user && !user.onboardingComplete) {
    return <SetupWizard />
  }

  const requiredCount = PROVIDERS.filter((p) => p.required).length
  const requiredConnected = PROVIDERS.filter(
    (p) => p.required && items.find((c) => c.provider === p.provider)?.status === 'connected',
  ).length

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Setup</h1>
        <p className="text-sm text-jarvis-muted mt-1">
          Connect the services Jarvis needs to run your business. Your keys are
          encrypted at rest with a key never exposed to your browser.
        </p>
      </div>

      <JarvisCard className="border-jarvis-blue/30">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-jarvis-muted">
              Onboarding progress
            </div>
            <div className="text-lg font-medium mt-1 tabular">
              {requiredConnected} of {requiredCount} required services connected
            </div>
          </div>
          <div
            className={cn(
              'text-sm px-3 py-1.5 rounded-lg',
              requiredConnected === requiredCount
                ? 'text-jarvis-green bg-jarvis-green/10'
                : 'text-jarvis-amber bg-jarvis-amber/10',
            )}
          >
            {requiredConnected === requiredCount ? 'Ready' : 'Action needed'}
          </div>
        </div>
        <div className="mt-3 h-1.5 bg-jarvis-surface-2 rounded-full overflow-hidden">
          <div
            className="h-full bg-jarvis-blue transition-all"
            style={{ width: `${(requiredConnected / requiredCount) * 100}%` }}
          />
        </div>
      </JarvisCard>

      {isLoading && (
        <JarvisCard>
          <div className="text-jarvis-muted text-sm py-8 text-center">Loading…</div>
        </JarvisCard>
      )}

      {!isLoading &&
        PROVIDERS.map((spec) => (
          <ProviderCard
            key={spec.provider}
            spec={spec}
            current={items.find((c) => c.provider === spec.provider)}
          />
        ))}

      {!isLoading && (
        <GoogleCard current={items.find((c) => c.provider === 'google')} />
      )}

      {!isLoading && <VoiceCard />}

      {!isLoading && <ComingSoonStrip />}
    </div>
  )
}
